import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthenticatedFetch } from './hooks/useAuthenticatedFetch';
import { useLocationProfile } from './hooks/useLocationProfile';
import LocationActivityPanel from './components/locations/LocationActivityPanel';
import LocationAuditPanel from './components/locations/LocationAuditPanel';
import LocationContactsPanel from './components/locations/LocationContactsPanel';
import LocationMediaPanel from './components/locations/LocationMediaPanel';
import LocationProfileHeader from './components/locations/LocationProfileHeader';
import { getLocationStatusSummary } from './components/locations/locationStatus';

const navItems = [
  { id: 'activity', label: 'Activity' },
  { id: 'media', label: 'Media' },
  { id: 'contacts', label: 'Contacts' }
];

const readJson = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
};

const LocationPage = ({ isAdmin = false, isSuperAdmin = false }) => {
  const { locationId } = useParams();
  const authenticatedFetch = useAuthenticatedFetch();
  const {
    loading,
    error,
    location,
    activeIssues,
    contacts,
    ticketLinks,
    sectionErrors,
    assets: profileAssets,
    uploadAsset,
    updateProfile,
    createContact,
    updateContact,
    deleteContact,
    createTicketLink,
    deleteTicketLink,
    deleteAsset,
    setPrimaryAsset
  } = useLocationProfile(locationId);
  const [assetState, setAssetState] = useState({ locationId: null, loading: false, error: '', assets: [] });
  const profileAssetList = useMemo(() => profileAssets || [], [profileAssets]);

  useEffect(() => {
    let isMounted = true;

    if (!locationId || loading || !location) {
      return undefined;
    }

    if (profileAssetList.length > 0) {
      setAssetState({ locationId, loading: false, error: '', assets: profileAssetList });
      return undefined;
    }

    const loadAssets = async () => {
      setAssetState({ locationId, loading: true, error: '', assets: [] });
      try {
        const response = await authenticatedFetch(`/api/locations/profiles/${locationId}/assets`);
        const data = await readJson(response);
        if (!response.ok) throw new Error(data.error || 'Failed to load location media');
        if (isMounted) setAssetState({ locationId, loading: false, error: '', assets: data.assets || [] });
      } catch (error) {
        if (isMounted) setAssetState({ locationId, loading: false, error: error.message, assets: [] });
      }
    };

    loadAssets();

    return () => {
      isMounted = false;
    };
  }, [authenticatedFetch, loading, location, locationId, profileAssetList]);

  const statusSummary = useMemo(() => getLocationStatusSummary(activeIssues), [activeIssues]);
  const sections = isSuperAdmin ? navItems.concat({ id: 'audit', label: 'Audit' }) : navItems;
  const assetStateMatchesRoute = assetState.locationId === locationId;
  const endpointAssets = assetStateMatchesRoute ? assetState.assets : [];
  const endpointAssetError = assetStateMatchesRoute ? assetState.error : '';
  const endpointAssetsLoading = assetStateMatchesRoute ? assetState.loading : false;
  const assets = profileAssetList.length > 0 ? profileAssetList : endpointAssets;

  if (loading) {
    return (
      <div className="h-full overflow-y-auto bg-black text-white">
        <div className="mx-auto max-w-7xl px-4 py-5">
          <div className="rounded-lg border border-white/10 bg-[#0b0b0b] p-6 text-sm text-gray-400">Loading location...</div>
        </div>
      </div>
    );
  }

  if (error || !location) {
    return (
      <div className="h-full overflow-y-auto bg-black text-white">
        <div className="mx-auto max-w-7xl px-4 py-5">
          <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-6 text-sm text-red-100">
            {error || 'Location not found'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-black text-white">
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-5">
        <LocationProfileHeader
          location={location}
          statusSummary={statusSummary}
          isAdmin={isAdmin}
          updateProfile={updateProfile}
        />

        <nav aria-label="Location sections" className="flex flex-wrap gap-2 rounded-lg border border-white/10 bg-[#0b0b0b] p-2">
          {sections.map(section => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className="rounded-lg px-3 py-2 text-xs font-semibold text-gray-300 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              {section.label}
            </a>
          ))}
        </nav>

        <LocationActivityPanel
          activeIssues={activeIssues}
          ticketLinks={ticketLinks}
          sectionError={sectionErrors.activity}
          isAdmin={isAdmin}
          createTicketLink={createTicketLink}
          deleteTicketLink={deleteTicketLink}
        />
        {endpointAssetError && (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
            {endpointAssetError}
          </div>
        )}
        {endpointAssetsLoading && assets.length === 0 ? (
          <section id="media" className="rounded-lg border border-white/10 bg-[#0b0b0b] p-4 text-sm text-gray-500">
            Loading media...
          </section>
        ) : (
          <LocationMediaPanel
            location={location}
            assets={assets}
            isAdmin={isAdmin}
            isSuperAdmin={isSuperAdmin}
            uploadAsset={uploadAsset}
            deleteAsset={deleteAsset}
            setPrimaryAsset={setPrimaryAsset}
          />
        )}
        <LocationContactsPanel
          contacts={contacts}
          isAdmin={isAdmin}
          isSuperAdmin={isSuperAdmin}
          createContact={createContact}
          updateContact={updateContact}
          deleteContact={deleteContact}
        />
        {isSuperAdmin && <LocationAuditPanel locationId={location.id} />}
      </div>
    </div>
  );
};

export default LocationPage;
